import * as React from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import { useTranslation } from 'react-i18next'
import { SxProps } from '@mui/material'
import flatten from 'lodash/flatten'

import { useAppDispatch, useAppSelector } from 'redux/Hook'
import {
  toggleModal as closeModal,
  updateQuestions,
  updateSelectedQuestions,
  saveQuestions,
  revertToSavedQuestions,
} from 'redux/slices/SignUpQuestions'
import { fetchSignUpFormQuestions } from 'api/SignUpFormQuestion/Action'
import {
  formatSignUpQuestion,
  getDateQuestionSelectionFormat,
  resetDateErrors,
} from 'components/Participants/Filters/SignUpQuestions/Helper'
import Questions from 'components/Participants/Filters/SignUpQuestions/Questions'
import { SIGN_UP_DATE_QUESTION } from 'lib/participants/Constants'
import { toggleHasFilterValueStatus } from 'redux/slices/FilterBuilder'
import { dialogActionsStyle } from 'components/Participants/Filters/Styles'

const dialogStyles: SxProps = {
  sx: {
    minWidth: {
      sm: 320,
      md: 900,
    },
    minHeight: {
      sm: 620,
      md: 568,
    },
  },
}

const SignUpQuestions = () => {
  const {
    showModal,
    questions,
    selectedQuestions: storeSelectedQuestions,
  } = useAppSelector((state) => state.signUpQuestions)

  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const fetchQuestions = async () => {
    if (questions.length) {
      /**
       * Don't fetch again.
       * Because we'er saving the previous selected state.
       */

      return
    }

    const result = await fetchSignUpFormQuestions()

    const formattedQuestions = formatSignUpQuestion(result)

    dispatch(updateQuestions(formattedQuestions))
  }

  React.useEffect(() => {
    fetchQuestions()
  }, [])

  const handleCancel = () => {
    dispatch(revertToSavedQuestions())
    dispatch(closeModal())
  }

  const handleApply = () => {
    const selectedQuestions = questions
      .map(({ question, value, id, type, yearOnlyDate, selected }) => {
        if (value?.length) {
          if (type === SIGN_UP_DATE_QUESTION) {
            return getDateQuestionSelectionFormat({
              question,
              value,
              id,
              type,
              yearOnlyDate,
              selected,
            })
          }

          return {
            id: id,
            question: question,
            value,
          }
        }
      })
      .filter(Boolean) as []

    const flattenSelectedQuestions = flatten(selectedQuestions)

    const hasNotQuestionsSelectedBefore = !storeSelectedQuestions.length
    const hasNotQuestionsSelectedNow = !flattenSelectedQuestions.length

    const doNotUpdateGridQuery =
      hasNotQuestionsSelectedBefore && hasNotQuestionsSelectedNow

    if (doNotUpdateGridQuery) {
      /**
       * ⚠️ "doNotUpdateGridQuery"
       * "true" => Means the modal questions empty state is similar to the initial one.
       * "false" => Initially its empty and later we can make it empty again
       * but in that case we have to update the "query".
       */

      dispatch(closeModal())

      return
    }

    dispatch(updateSelectedQuestions(flattenSelectedQuestions))
    const questionsWithResetDateErrors = resetDateErrors(questions)

    dispatch(updateQuestions(questionsWithResetDateErrors))

    dispatch(saveQuestions())

    /**
     * This is needed to trigger the segment API, as this
     * tells that the signup filter value has changed
     */
    dispatch(toggleHasFilterValueStatus(true))

    /**
     * Updating the selectedQuestions in use-Effect of participant container will pick it up
     * and update the grid data.
     */
    dispatch(closeModal())
  }

  return (
    <Dialog
      open={showModal}
      aria-labelledby={t('participant.signUpQuestions.title')}
      aria-describedby={t('participant.signUpQuestions.subTitle')}
      PaperProps={dialogStyles}
    >
      <DialogTitle sx={{ fontSize: 24, pt: 4, pb: 3 }}>
        {t('participant.signUpQuestions.title')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText pb={3}>
          {t('participant.signUpQuestions.subTitle')}
        </DialogContentText>

        <Questions />
      </DialogContent>
      <DialogActions sx={{ p: 3, ...dialogActionsStyle }}>
        <Button sx={{ mr: 1 }} onClick={handleCancel}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={!questions.length}
        >
          {t('participant.signUpQuestions.applyBtnText')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SignUpQuestions
